import { departmentMatches } from './quotaDirectory';
import type {
  AccessPolicy,
  ActivityDecision,
  ActivityEvent,
  ClientStatus,
  ClientType,
  ExternalMcpConnection,
  GatewayApiKey,
  McpClient,
  PolicyEffect,
  PolicyStatus,
  PublishedCapability,
  QuotaAssignment,
  QuotaDirectory,
  QuotaDirectoryUser,
  QuotaSource,
  QuotaUsage,
  QuotaUser,
} from './governedMcpTypes';
import { buildEffectiveQuotaUsers, currentQuotaMonth } from './quotaUsage';

export type DashboardRange = '24h' | '7d' | '30d' | '90d';

export interface DashboardFilters {
  departmentId: string;
  clientId: string;
  toolId: string;
}

export const DEFAULT_FILTERS: DashboardFilters = {
  departmentId: 'all',
  clientId: 'all',
  toolId: 'all',
};

export const DASHBOARD_SECTIONS = [
  { id: 'org-overview', n: 1, label: 'Organization' },
  { id: 'user-analytics', n: 2, label: 'Users' },
  { id: 'team-analytics', n: 3, label: 'Teams' },
  { id: 'token-analytics', n: 4, label: 'Tokens' },
  { id: 'tool-analytics', n: 5, label: 'Tools' },
  { id: 'usage-limits', n: 6, label: 'Limits' },
  { id: 'bulk-coverage', n: 7, label: 'Coverage' },
  { id: 'access-policies', n: 8, label: 'Policies' },
  { id: 'security-audit', n: 9, label: 'Audit' },
  { id: 'integrations', n: 10, label: 'Integrations' },
] as const;

export interface DashboardSource {
  activity: ActivityEvent[];
  published: PublishedCapability[];
  clients: McpClient[];
  policies: AccessPolicy[];
  apiKeys: GatewayApiKey[];
  quotaAssignments: QuotaAssignment[];
  quotaUsages: QuotaUsage[];
  quotaDirectory: QuotaDirectory;
  externalMcp: ExternalMcpConnection[];
}

export interface NamedCount {
  key: string;
  name: string;
  value: number;
}

export interface SeriesPoint {
  key: string;
  label: string;
  value: number;
}

export interface DualTrendPoint {
  key: string;
  label: string;
  calls: number;
  tokens: number;
}

export interface DecisionTrendPoint {
  key: string;
  label: string;
  allowed: number;
  denied: number;
  approval_required: number;
  failed: number;
}

export interface UserAnalyticsRow {
  userId: string;
  name: string;
  email: string;
  department: string;
  role: string;
  calls: number;
  tokens: number;
  topTool: string;
}

export interface TeamAnalyticsRow {
  departmentId: string;
  name: string;
  headcount: number;
  calls: number;
  tokens: number;
}

export interface ToolAnalyticsRow {
  toolId: string;
  name: string;
  executions: number;
  share: number;
  failures: number;
  denials: number;
  p50: number;
  p95: number;
}

export interface AuditRow {
  id: string;
  timestamp: string;
  userName: string;
  clientName: string;
  toolName: string;
  decision: ActivityDecision;
  durationMs: number;
}

export interface PolicySummaryRow {
  id: string;
  name: string;
  effect: PolicyEffect;
  status: PolicyStatus;
  toolCount: number;
  userCount: number;
}

export interface ClientIntegrationRow {
  id: string;
  name: string;
  type: ClientType;
  status: ClientStatus;
  lastConnectedAt: string | null;
}

export interface ExternalServerRow {
  id: string;
  name: string;
  publisher: string;
  tools: number;
  resources: number;
}

export interface DashboardMetrics {
  range: DashboardRange;
  rangeLabel: string;
  organization: {
    totalUsers: number;
    mcpCalls: number;
    estimatedTokens: number;
    activeIntegrations: number;
    activeClients: number;
    publishedTools: number;
    trend: DualTrendPoint[];
  };
  userAnalytics: {
    withUsage: number;
    avgCalls: number;
    avgTokens: number;
    bands: NamedCount[];
    concentration: NamedCount[];
  };
  teams: TeamAnalyticsRow[];
  tokens: {
    total: number;
    monthlyUsed: number;
    monthlyLimit: number;
    monthlyShare: number;
    trend: SeriesPoint[];
    byModel: NamedCount[];
  };
  tools: ToolAnalyticsRow[];
  limits: {
    monthlyLimit: number;
    monthlyUsed: number;
    remaining: number;
    overLimit: number;
    warning: number;
    rateLimitedTools: number;
    publishedTools: number;
    mix: NamedCount[];
  };
  coverage: {
    totalEmployees: number;
    limitSource: NamedCount[];
    policy: NamedCount[];
  };
  policies: {
    allow: number;
    deny: number;
    requireApproval: number;
    active: number;
    disabled: number;
    draft: number;
    namedTools: number;
    unnamedTools: number;
    deniedCalls: number;
    effectMix: NamedCount[];
    statusMix: NamedCount[];
  };
  audit: {
    allowed: number;
    denied: number;
    approvalRequired: number;
    failed: number;
    trend: DecisionTrendPoint[];
  };
  integrations: {
    clients: ClientIntegrationRow[];
    byType: NamedCount[];
    byStatus: NamedCount[];
    publishedTools: number;
    publishedResources: number;
    publishedPrompts: number;
    external: ExternalServerRow[];
  };
}

const RANGE_MS: Record<DashboardRange, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '90d': 90 * 24 * 60 * 60 * 1000,
};

const RANGE_LABEL: Record<DashboardRange, string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
};

const LIMIT_SOURCE_ORDER: QuotaSource[] = ['user', 'role', 'department', 'none'];
const LIMIT_SOURCE_LABEL: Record<QuotaSource, string> = {
  user: 'Direct user limit',
  role: 'Inherited role limit',
  department: 'Inherited department limit',
  none: 'No limit',
};

const CLIENT_STATUS_LABEL: Record<ClientStatus, string> = {
  active: 'Active',
  disabled: 'Disabled',
  revoked: 'Revoked',
};

export function compactCount(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${Math.round(value / 1000)}k`;
  if (abs >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return new Intl.NumberFormat('en-US').format(Math.round(value));
}

export function rangeWindow(range: DashboardRange, now = new Date()): { start: Date; end: Date } {
  return { start: new Date(now.getTime() - RANGE_MS[range]), end: now };
}

function isoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseEventTime(value: string): number {
  const ts = Date.parse(value);
  return Number.isFinite(ts) ? ts : 0;
}

function inRange(ts: number, start: Date, end: Date): boolean {
  return ts >= start.getTime() && ts <= end.getTime();
}

function dateInRange(iso: string, start: Date, end: Date): boolean {
  if (!iso) return false;
  const day = iso.length >= 10 ? iso.slice(0, 10) : iso;
  return day >= isoDate(start) && day <= isoDate(end);
}

function percentile(values: number[], p: number): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const pos = (sorted.length - 1) * p;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return Math.round(sorted[lo]);
  return Math.round(sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo));
}

function departmentOf(
  directory: QuotaDirectory,
  departmentId: string | undefined
): { id: string; name: string } {
  if (!departmentId) return { id: '', name: 'Unassigned' };
  const match = directory.departments.find(
    (item) => item.id === departmentId || departmentMatches(item, departmentId)
  );
  return { id: match?.id || departmentId, name: match?.name || 'Unassigned' };
}

function userMatchesDepartment(
  departmentId: string | undefined,
  filterId: string,
  directory: QuotaDirectory
): boolean {
  if (!filterId || filterId === 'all') return true;
  const department = directory.departments.find(
    (item) => item.id === filterId || departmentMatches(item, filterId)
  );
  if (!department) return departmentId === filterId;
  return department.matchIds.includes(departmentId || '') || departmentId === department.id;
}

function directoryUserById(directory: QuotaDirectory, userId: string): QuotaDirectoryUser | undefined {
  return directory.users.find((item) => item.id === userId);
}

function mostSpecificSource(sources: QuotaSource[]): QuotaSource {
  for (const source of LIMIT_SOURCE_ORDER) {
    if (sources.includes(source)) return source;
  }
  return 'none';
}

function policyCoversUser(policy: AccessPolicy, userId: string): boolean {
  if (!policy.userIds.length && !policy.groupIds.length) return true;
  return policy.userIds.includes(userId);
}

function formatTrendLabel(range: DashboardRange, date: Date): string {
  if (range === '24h') {
    return `${String(date.getHours()).padStart(2, '0')}:00`;
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface TimeBucket {
  key: string;
  label: string;
  start: Date;
  end: Date;
}

function buildTimeBuckets(range: DashboardRange, start: Date, end: Date): TimeBucket[] {
  if (range === '24h') {
    return Array.from({ length: 24 }, (_, index) => {
      const at = new Date(end.getTime() - (23 - index) * 60 * 60 * 1000);
      at.setMinutes(0, 0, 0);
      return {
        key: at.toISOString(),
        label: formatTrendLabel(range, at),
        start: at,
        end: new Date(at.getTime() + 60 * 60 * 1000),
      };
    });
  }
  const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)));
  const step = range === '90d' ? 7 : 1;
  const buckets: TimeBucket[] = [];
  for (let index = days - 1; index >= 0; index -= step) {
    const at = new Date(end.getFullYear(), end.getMonth(), end.getDate() - index);
    const bucketEnd = new Date(at.getFullYear(), at.getMonth(), at.getDate() + step);
    buckets.push({
      key: isoDate(at),
      label: formatTrendLabel(range, at),
      start: at,
      end: bucketEnd.getTime() > end.getTime() ? new Date(end.getTime() + 1) : bucketEnd,
    });
  }
  return buckets;
}

function bucketIndexForTime(buckets: TimeBucket[], ts: number): number {
  for (let index = 0; index < buckets.length; index += 1) {
    const bucket = buckets[index];
    if (ts >= bucket.start.getTime() && ts < bucket.end.getTime()) return index;
  }
  return -1;
}

function bucketIndexForDate(buckets: TimeBucket[], iso: string): number {
  const day = iso.slice(0, 10);
  const ts = Date.parse(`${day}T12:00:00`);
  if (!Number.isFinite(ts)) return -1;
  return bucketIndexForTime(buckets, ts);
}

function buildTokenTrend(
  buckets: TimeBucket[],
  usages: { userId: string; clientId: string; dailyUsage: { date: string; tokens: number }[] }[],
  userFilter: Set<string> | null,
  clientId: string
): SeriesPoint[] {
  const values = buckets.map(() => 0);
  usages.forEach((item) => {
    if (clientId !== 'all' && item.clientId !== clientId) return;
    if (userFilter && !userFilter.has(item.userId)) return;
    item.dailyUsage.forEach((point) => {
      const index = bucketIndexForDate(buckets, point.date);
      if (index < 0) return;
      values[index] += Number(point.tokens) || 0;
    });
  });
  return buckets.map((bucket, index) => ({ key: bucket.key, label: bucket.label, value: values[index] }));
}

function buildCallTrend(buckets: TimeBucket[], activity: ActivityEvent[]): SeriesPoint[] {
  const values = buckets.map(() => 0);
  activity.forEach((event) => {
    const index = bucketIndexForTime(buckets, parseEventTime(event.timestamp));
    if (index < 0) return;
    values[index] += 1;
  });
  return buckets.map((bucket, index) => ({ key: bucket.key, label: bucket.label, value: values[index] }));
}

function buildDecisionTrend(buckets: TimeBucket[], activity: ActivityEvent[]): DecisionTrendPoint[] {
  const rows = buckets.map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
    allowed: 0,
    denied: 0,
    approval_required: 0,
    failed: 0,
  }));
  activity.forEach((event) => {
    const index = bucketIndexForTime(buckets, parseEventTime(event.timestamp));
    if (index < 0) return;
    rows[index][event.decision] += 1;
  });
  return rows;
}

function tokenBand(tokens: number): { key: string; name: string } {
  if (tokens <= 0) return { key: 'none', name: 'No tokens' };
  if (tokens < 10_000) return { key: 'low', name: 'Under 10k' };
  if (tokens < 100_000) return { key: 'mid', name: '10k–100k' };
  if (tokens < 500_000) return { key: 'high', name: '100k–500k' };
  return { key: 'very-high', name: '500k+' };
}

function tokensInRangeForUser(
  usages: QuotaUsage[],
  userId: string,
  clientId: string,
  start: Date,
  end: Date
): number {
  return usages
    .filter((item) => item.userId === userId && (clientId === 'all' || item.clientId === clientId))
    .reduce((sum, item) => {
      return (
        sum +
        item.dailyUsage
          .filter((point) => dateInRange(point.date, start, end))
          .reduce((inner, point) => inner + (Number(point.tokens) || 0), 0)
      );
    }, 0);
}

function topToolForUser(events: ActivityEvent[], userId: string): string {
  const counts = new Map<string, number>();
  for (const event of events) {
    if (event.userId !== userId) continue;
    const name = event.capabilityName || 'Unknown tool';
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  let best = '—';
  let max = 0;
  counts.forEach((count, name) => {
    if (count > max) {
      max = count;
      best = name;
    }
  });
  return best;
}

export function buildDashboardMetrics(
  range: DashboardRange,
  filters: DashboardFilters,
  source: DashboardSource
): DashboardMetrics {
  const { start, end } = rangeWindow(range);
  const month = currentQuotaMonth();
  const departmentId = filters.departmentId || 'all';
  const clientId = filters.clientId || 'all';
  const toolId = filters.toolId || 'all';

  const directoryUsers = source.quotaDirectory.users.filter((user) =>
    userMatchesDepartment(user.departmentId, departmentId, source.quotaDirectory)
  );
  const directoryIds = new Set(directoryUsers.map((user) => user.id));

  const monthUsers = buildEffectiveQuotaUsers(
    source.apiKeys,
    source.quotaAssignments,
    source.quotaUsages,
    source.quotaDirectory,
    month
  ).filter((user) => {
    if (!userMatchesDepartment(user.departmentId, departmentId, source.quotaDirectory)) return false;
    if (clientId !== 'all' && user.clientId !== clientId) return false;
    return true;
  });

  const activity = source.activity.filter((event) => {
    if (!inRange(parseEventTime(event.timestamp), start, end)) return false;
    if (clientId !== 'all' && event.clientId !== clientId) return false;
    if (toolId !== 'all' && event.capabilityId !== toolId) return false;
    if (departmentId !== 'all') {
      const dirUser = directoryUserById(source.quotaDirectory, event.userId);
      if (!dirUser || !directoryIds.has(dirUser.id)) return false;
    }
    return true;
  });

  const toolUserIds =
    toolId === 'all' ? null : new Set(activity.map((event) => event.userId).filter(Boolean));

  const scopedMonthUsers =
    toolUserIds === null
      ? monthUsers
      : monthUsers.filter((user) => toolUserIds.has(user.userId || ''));

  const people = new Map<
    string,
    {
      userId: string;
      name: string;
      email: string;
      departmentId: string;
      role: string;
      sources: QuotaSource[];
      monthlyLimit: number;
      monthlyUsed: number;
    }
  >();

  const seedPeople = (userId: string, fallback?: Partial<QuotaUser> & { departmentId?: string }) => {
    if (!userId || people.has(userId)) return;
    const dir = directoryUserById(source.quotaDirectory, userId);
    if (
      departmentId !== 'all' &&
      !userMatchesDepartment(dir?.departmentId || fallback?.departmentId, departmentId, source.quotaDirectory)
    ) {
      return;
    }
    people.set(userId, {
      userId,
      name: dir?.name || fallback?.name || userId,
      email: dir?.email || fallback?.email || '',
      departmentId: dir?.departmentId || fallback?.departmentId || '',
      role: dir?.roleName || fallback?.role || '',
      sources: [],
      monthlyLimit: 0,
      monthlyUsed: 0,
    });
  };

  if (clientId === 'all' && toolId === 'all') {
    for (const user of directoryUsers) seedPeople(user.id, user);
  }
  for (const user of scopedMonthUsers) seedPeople(user.userId || '', user);
  for (const event of activity) seedPeople(event.userId);
  for (const usage of source.quotaUsages) {
    if (clientId !== 'all' && usage.clientId !== clientId) continue;
    if (toolUserIds && !toolUserIds.has(usage.userId)) continue;
    seedPeople(usage.userId);
  }

  for (const user of scopedMonthUsers) {
    const row = people.get(user.userId || '');
    if (!row) continue;
    row.sources.push(user.source || 'none');
    row.monthlyLimit += user.tokenLimit;
    row.monthlyUsed += user.tokensUsed;
  }

  const orgUsers = Array.from(people.values());
  const peopleIds = new Set(orgUsers.map((person) => person.userId));

  const estimatedTokens = Array.from(people.values()).reduce(
    (sum, person) => sum + tokensInRangeForUser(source.quotaUsages, person.userId, clientId, start, end),
    0
  );

  const publishedLive = source.published.filter((item) => item.status === 'published');
  const publishedInFilter =
    toolId === 'all' ? publishedLive : publishedLive.filter((item) => item.id === toolId);
  const clientsInFilter =
    clientId === 'all' ? source.clients : source.clients.filter((item) => item.id === clientId);
  const activeClients = clientsInFilter.filter((item) => item.status === 'active').length;
  const publishedTools = publishedInFilter.filter(
    (item) => item.mcpCapability === 'tool' || item.mcpRepresentations.includes('tool')
  ).length;

  const callsByUser = new Map<string, number>();
  for (const event of activity) {
    callsByUser.set(event.userId, (callsByUser.get(event.userId) ?? 0) + 1);
  }

  const userRows: UserAnalyticsRow[] = Array.from(people.values())
    .map((person) => {
      const dept = departmentOf(source.quotaDirectory, person.departmentId);
      return {
        userId: person.userId,
        name: person.name,
        email: person.email,
        department: dept.name,
        role: person.role,
        calls: callsByUser.get(person.userId) ?? 0,
        tokens: tokensInRangeForUser(source.quotaUsages, person.userId, clientId, start, end),
        topTool: topToolForUser(activity, person.userId),
      };
    })
    .filter((row) => row.calls > 0 || row.tokens > 0)
    .sort((left, right) => right.tokens - left.tokens || right.calls - left.calls);

  const withUsage = userRows.length;
  const avgCalls = withUsage ? Math.round(userRows.reduce((sum, row) => sum + row.calls, 0) / withUsage) : 0;
  const avgTokens = withUsage ? Math.round(userRows.reduce((sum, row) => sum + row.tokens, 0) / withUsage) : 0;
  const bandMap = new Map<string, NamedCount>();
  ['none', 'low', 'mid', 'high', 'very-high'].forEach((key) => {
    const named = tokenBand(key === 'none' ? 0 : key === 'low' ? 1 : key === 'mid' ? 10_000 : key === 'high' ? 100_000 : 500_000);
    bandMap.set(named.key, { key: named.key, name: named.name, value: 0 });
  });
  userRows.forEach((row) => {
    const band = tokenBand(row.tokens);
    const current = bandMap.get(band.key) || { key: band.key, name: band.name, value: 0 };
    current.value += 1;
    bandMap.set(band.key, current);
  });
  const topShareCount = withUsage ? Math.max(1, Math.ceil(withUsage * 0.1)) : 0;
  const topTokens = userRows.slice(0, topShareCount).reduce((sum, row) => sum + row.tokens, 0);
  const restTokens = Math.max(0, userRows.reduce((sum, row) => sum + row.tokens, 0) - topTokens);

  const teamMap = new Map<string, TeamAnalyticsRow>();
  const ensureTeam = (departmentKey: string, name: string) => {
    const existing = teamMap.get(departmentKey);
    if (existing) return existing;
    const next: TeamAnalyticsRow = { departmentId: departmentKey, name, headcount: 0, calls: 0, tokens: 0 };
    teamMap.set(departmentKey, next);
    return next;
  };
  for (const person of orgUsers) {
    const dept = departmentOf(source.quotaDirectory, person.departmentId);
    const row = ensureTeam(dept.id || 'unassigned', dept.name);
    row.headcount += 1;
  }
  for (const row of userRows) {
    const person = people.get(row.userId);
    const dept = departmentOf(source.quotaDirectory, person?.departmentId);
    const team = ensureTeam(dept.id || 'unassigned', dept.name);
    team.calls += row.calls;
    team.tokens += row.tokens;
  }
  const teams = Array.from(teamMap.values()).sort((left, right) => right.tokens - left.tokens || right.calls - left.calls);

  const monthlyLimit = scopedMonthUsers.reduce((sum, user) => sum + user.tokenLimit, 0);
  const monthlyUsed = scopedMonthUsers.reduce((sum, user) => sum + user.tokensUsed, 0);
  const modelMap = new Map<string, number>();
  for (const user of scopedMonthUsers) {
    for (const row of user.byModel) {
      modelMap.set(row.model, (modelMap.get(row.model) ?? 0) + row.tokens);
    }
  }
  const modelTotal = Array.from(modelMap.values()).reduce((sum, value) => sum + value, 0);
  const byModel: NamedCount[] =
    modelTotal <= 0 || estimatedTokens <= 0
      ? Array.from(modelMap.entries())
          .map(([name, value]) => ({ key: name, name, value }))
          .sort((left, right) => right.value - left.value)
      : Array.from(modelMap.entries())
          .map(([name, value]) => ({
            key: name,
            name,
            value: Math.round((value / modelTotal) * estimatedTokens),
          }))
          .sort((left, right) => right.value - left.value);

  const toolMap = new Map<string, { name: string; durations: number[]; failures: number; denials: number; executions: number }>();
  for (const event of activity) {
    const id = event.capabilityId || event.capabilityName || 'unknown';
    const existing = toolMap.get(id) || {
      name: event.capabilityName || 'Unknown tool',
      durations: [] as number[],
      failures: 0,
      denials: 0,
      executions: 0,
    };
    existing.executions += 1;
    if (Number.isFinite(event.durationMs)) existing.durations.push(event.durationMs);
    if (event.decision === 'failed') existing.failures += 1;
    if (event.decision === 'denied') existing.denials += 1;
    toolMap.set(id, existing);
  }
  const toolTotal = activity.length || 1;
  const tools: ToolAnalyticsRow[] = Array.from(toolMap.entries())
    .map(([toolKey, row]) => ({
      toolId: toolKey,
      name: row.name,
      executions: row.executions,
      share: Math.round((row.executions / toolTotal) * 100),
      failures: row.failures,
      denials: row.denials,
      p50: percentile(row.durations, 0.5),
      p95: percentile(row.durations, 0.95),
    }))
    .sort((left, right) => right.executions - left.executions);

  const overLimit = scopedMonthUsers.filter((user) => user.tokenLimit > 0 && user.tokensUsed >= user.tokenLimit).length;
  const warning = scopedMonthUsers.filter((user) => {
    if (user.tokenLimit <= 0) return false;
    const ratio = user.tokensUsed / user.tokenLimit;
    return ratio >= 0.8 && ratio < 1;
  }).length;
  const onTrack = Math.max(0, scopedMonthUsers.length - overLimit - warning);
  const rateLimitedTools = publishedInFilter.filter((item) => item.rateLimitPerMinute > 0).length;

  const coveragePeople =
    clientId === 'all' && toolId === 'all'
      ? directoryUsers.map((user) => {
          const existing = people.get(user.id);
          if (existing) return existing;
          return {
            userId: user.id,
            name: user.name,
            email: user.email,
            departmentId: user.departmentId,
            role: user.roleName,
            sources: ['none' as QuotaSource],
            monthlyLimit: 0,
            monthlyUsed: 0,
          };
        })
      : orgUsers;

  const sourceCounts: Record<QuotaSource, number> = { user: 0, role: 0, department: 0, none: 0 };
  for (const person of coveragePeople) {
    const sourceKind = mostSpecificSource(person.sources.length ? person.sources : ['none']);
    sourceCounts[sourceKind] += 1;
  }

  const activePolicies = source.policies.filter((policy) => policy.status === 'active');
  let inPolicy = 0;
  for (const person of coveragePeople) {
    if (activePolicies.some((policy) => policyCoversUser(policy, person.userId))) inPolicy += 1;
  }

  const namedToolIds = new Set(source.policies.flatMap((policy) => policy.publishedCapabilityIds));
  const namedTools = publishedLive.filter((item) => namedToolIds.has(item.id)).length;
  const unnamedTools = publishedLive.length - namedTools;

  const decisionCounts: Record<ActivityDecision, number> = {
    allowed: 0,
    denied: 0,
    approval_required: 0,
    failed: 0,
  };
  for (const event of activity) decisionCounts[event.decision] += 1;

  const timeBuckets = buildTimeBuckets(range, start, end);
  const tokenTrend = buildTokenTrend(timeBuckets, source.quotaUsages, peopleIds, clientId);
  const callTrend = buildCallTrend(timeBuckets, activity);
  const decisionTrend = buildDecisionTrend(timeBuckets, activity);
  const orgTrend: DualTrendPoint[] = timeBuckets.map((bucket, index) => ({
    key: bucket.key,
    label: bucket.label,
    calls: callTrend[index]?.value ?? 0,
    tokens: tokenTrend[index]?.value ?? 0,
  }));

  const byTypeMap = new Map<string, number>();
  const byStatusMap = new Map<ClientStatus, number>();
  for (const client of clientsInFilter) {
    byTypeMap.set(client.clientType, (byTypeMap.get(client.clientType) ?? 0) + 1);
    byStatusMap.set(client.status, (byStatusMap.get(client.status) ?? 0) + 1);
  }

  const publishedResources = publishedInFilter.filter(
    (item) => item.mcpCapability === 'resource' || item.mcpRepresentations.includes('resource')
  ).length;
  const publishedPrompts = publishedInFilter.filter(
    (item) => item.mcpCapability === 'prompt' || item.mcpRepresentations.includes('task')
  ).length;

  return {
    range,
    rangeLabel: RANGE_LABEL[range],
    organization: {
      totalUsers: orgUsers.length,
      mcpCalls: activity.length,
      estimatedTokens,
      activeIntegrations: activeClients + publishedTools,
      activeClients,
      publishedTools,
      trend: orgTrend,
    },
    userAnalytics: {
      withUsage,
      avgCalls,
      avgTokens,
      bands: Array.from(bandMap.values()),
      concentration: [
        { key: 'top', name: 'Top 10% of users', value: topTokens },
        { key: 'rest', name: 'Everyone else', value: restTokens },
      ],
    },
    teams,
    tokens: {
      total: estimatedTokens,
      monthlyUsed,
      monthlyLimit,
      monthlyShare: monthlyLimit > 0 ? Math.min(100, Math.round((monthlyUsed / monthlyLimit) * 100)) : 0,
      trend: tokenTrend,
      byModel,
    },
    tools,
    limits: {
      monthlyLimit,
      monthlyUsed,
      remaining: Math.max(0, monthlyLimit - monthlyUsed),
      overLimit,
      warning,
      rateLimitedTools,
      publishedTools: publishedInFilter.length,
      mix: [
        { key: 'ok', name: 'On track', value: onTrack },
        { key: 'warning', name: 'Approaching 80%', value: warning },
        { key: 'exceeded', name: 'Over limit', value: overLimit },
      ],
    },
    coverage: {
      totalEmployees: coveragePeople.length,
      limitSource: LIMIT_SOURCE_ORDER.map((key) => ({
        key,
        name: LIMIT_SOURCE_LABEL[key],
        value: sourceCounts[key],
      })),
      policy: [
        { key: 'included', name: 'In an access policy', value: inPolicy },
        { key: 'excluded', name: 'Not in a policy', value: Math.max(0, coveragePeople.length - inPolicy) },
      ],
    },
    policies: {
      allow: source.policies.filter((item) => item.effect === 'allow').length,
      deny: source.policies.filter((item) => item.effect === 'deny').length,
      requireApproval: source.policies.filter((item) => item.effect === 'require_approval').length,
      active: source.policies.filter((item) => item.status === 'active').length,
      disabled: source.policies.filter((item) => item.status === 'disabled').length,
      draft: source.policies.filter((item) => item.status === 'draft').length,
      namedTools,
      unnamedTools,
      deniedCalls: decisionCounts.denied,
      effectMix: [
        { key: 'allow', name: 'Allow', value: source.policies.filter((item) => item.effect === 'allow').length },
        { key: 'deny', name: 'Deny', value: source.policies.filter((item) => item.effect === 'deny').length },
        { key: 'require_approval', name: 'Require approval', value: source.policies.filter((item) => item.effect === 'require_approval').length },
      ],
      statusMix: [
        { key: 'active', name: 'Active', value: source.policies.filter((item) => item.status === 'active').length },
        { key: 'draft', name: 'Draft', value: source.policies.filter((item) => item.status === 'draft').length },
        { key: 'disabled', name: 'Disabled', value: source.policies.filter((item) => item.status === 'disabled').length },
      ],
    },
    audit: {
      allowed: decisionCounts.allowed,
      denied: decisionCounts.denied,
      approvalRequired: decisionCounts.approval_required,
      failed: decisionCounts.failed,
      trend: decisionTrend,
    },
    integrations: {
      clients: clientsInFilter.map((client) => ({
        id: client.id,
        name: client.name,
        type: client.clientType,
        status: client.status,
        lastConnectedAt: client.lastConnectedAt,
      })),
      byType: Array.from(byTypeMap.entries()).map(([key, value]) => ({ key, name: key, value })),
      byStatus: (Object.keys(CLIENT_STATUS_LABEL) as ClientStatus[]).map((key) => ({
        key,
        name: CLIENT_STATUS_LABEL[key],
        value: byStatusMap.get(key) ?? 0,
      })),
      publishedTools,
      publishedResources,
      publishedPrompts,
      external: source.externalMcp.map((item) => ({
        id: item.id,
        name: item.name,
        publisher: item.publisher,
        tools: item.tools.length,
        resources: item.resources.length,
      })),
    },
  };
}
